import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Award, Download, Calendar, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const MyCertificates = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}` };

  useEffect(() => {
    fetchCertificates();
  }, []);

  const fetchCertificates = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/student/certificates`, { headers });
      if (res.ok) {
        const data = await res.json();
        setCertificates(data);
      }
    } catch (err) {
      console.error('Failed to fetch certificates:', err);
    }
    setLoading(false);
  };

  const handleDownload = async (certId, userName) => {
    try {
      const res = await fetch(`${API}/api/certificates/${certId}/download`, { headers });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `certificate_${userName.replace(/\s+/g, '_')}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } else {
        toast.error(isRTL ? 'فشل تحميل الشهادة' : 'Failed to download certificate');
      }
    } catch (err) {
      toast.error(isRTL ? 'خطأ في التحميل' : 'Download error');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div data-testid="my-certificates" className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Award className="h-7 w-7 text-cyan-600" />
          {isRTL ? 'شهاداتي' : 'My Certificates'}
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          {isRTL ? 'الشهادات الصادرة لك' : 'Certificates issued to you'}
        </p>
      </div>

      {certificates.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-16 text-center">
          <Award className="h-16 w-16 text-gray-200 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">{isRTL ? 'لا توجد شهادات حالياً' : 'No certificates yet'}</p>
          <p className="text-gray-400 text-sm mt-1">
            {isRTL ? 'ستظهر هنا الشهادات عند إصدارها لك' : 'Certificates will appear here when issued to you'}
          </p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {certificates.map((cert) => (
            <div
              key={cert.id}
              data-testid={`student-cert-${cert.id}`}
              className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow"
            >
              {/* Certificate card header */}
              <div className="bg-gradient-to-r from-blue-600 to-cyan-600 px-5 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                    <Award className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white text-sm">{cert.program_name}</h3>
                    <p className="text-xs text-white/70">{isRTL ? 'شهادة حضور' : 'Certificate of Attendance'}</p>
                  </div>
                </div>
              </div>
              {/* Certificate card body */}
              <div className="p-5 space-y-3">
                <div>
                  <p className="text-sm font-medium text-gray-800">{cert.user_name}</p>
                  <div className="flex items-center gap-1.5 text-xs text-gray-500 mt-1">
                    <Calendar className="h-3 w-3" />
                    {cert.completion_date}
                  </div>
                </div>
                <div className="text-xs text-gray-400">
                  {isRTL ? 'صادرة بواسطة:' : 'Issued by:'} {cert.issued_by_name}
                </div>
                <Button
                  data-testid={`download-student-cert-${cert.id}`}
                  onClick={() => handleDownload(cert.id, cert.user_name)}
                  className="w-full gap-2 bg-blue-600 hover:bg-blue-700"
                  size="sm"
                >
                  <Download className="h-4 w-4" />
                  {isRTL ? 'تحميل الشهادة PDF' : 'Download Certificate PDF'}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyCertificates;
